import React, { useCallback, useEffect, useState } from 'react';
import { BiSolidShareAlt, BiStreetView, BiWalk } from 'react-icons/bi';
import { useSetAtom } from 'jotai';
import { shareInfoAtom } from '../../atoms';
import './LocationInfo.css';
import ReactGA from 'react-ga4';
import { openReverseGeocoder } from '../../lib/reverseGeocoder';
import { GEOCODER_ERROR_MSG } from '../../constants/message';


type LocationInfoProps = {
    lngLat: [number, number] | undefined;
}

const LocationInfo: React.FC<LocationInfoProps> = ({ lngLat }) => {
    const setShareInfo = useSetAtom(shareInfoAtom);

    const [tooltipText, setTooltipText] = useState<string | undefined>(undefined);
    const [address, setAddress] = useState<string>('...取得中');


    /* *********************** 
     * シェアポップアップを開く
     * ***********************/
    const onClickShareBtn = useCallback((id: string | undefined) => {
        if (!lngLat) { return; }
        //【GoogleAnalytics】シェアボタン クリックトラッキング
        ReactGA.event({
            category: "button_click", // カテゴリ（任意の名前）
            action: "click",          // イベントの種類
            label: 'ポップアップ/シェア',       // ボタン名
        });

        setShareInfo({ id: id, lngLat: { lng: lngLat[0], lat: lngLat[1] }, address: address ?? '住所不明' });
    }, [lngLat, setShareInfo, address]);


    /* *********************** 
     * ストリートビューに遷移
     * ***********************/
    const onClickStreetViewBtn = (lng: number, lat: number) => {
        //【GoogleAnalytics】ストリートビュー クリックトラッキング
        ReactGA.event({
            category: "button_click", // カテゴリ（任意の名前）
            action: "click",          // イベントの種類
            label: 'ポップアップ/ストリートビュー',       // ボタン名
        });

        window.open(`https://www.google.com/maps/@?api=1&map_action=pano&parameters&viewpoint=${lat},${lng}&heading=-90`, '_blank');
    }


    /* *********************** 
     * ここへ行くでGoogleMapに遷移
     * ***********************/
    const onClickGoToHereBtn = (lng: number, lat: number) => {
        //【GoogleAnalytics】ここへ行く クリックトラッキング
        ReactGA.event({
            category: "button_click", // カテゴリ（任意の名前）
            action: "click",          // イベントの種類
            label: 'ポップアップ/ここへ行く',       // ボタン名
        });

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setTooltipText('GoogleMapを開きます');
                setTimeout(() => { setTooltipText(undefined); }, 2000);
                const { coords } = position;
                window.open(
                    `https://www.google.com/maps/dir/${coords.latitude},${coords.longitude}/${lat},${lng}`,
                    '_blank'
                );
            },
            (error) => {
                switch (error.code) {
                    case 1:
                        setTooltipText('位置情報の利用が許可されていません');
                        break;
                    case 2:
                        setTooltipText('デバイスの位置が判定できません');
                        break;
                    case 3:
                        setTooltipText('タイムアウトしました');
                        break;
                }
                setTimeout(() => { setTooltipText(undefined); }, 2000);
            }, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
        }
        );
    }

    /* ******************
    * リバースジオコーディングで町名を取得する
    * ******************/
    const getReverseAddress = useCallback(async (lngLat: { lng: number, lat: number }) => {
        const res = await openReverseGeocoder(
            (Array.isArray(lngLat) ? lngLat : [lngLat.lng, lngLat.lat]) as [number, number]
        );
        return (res?.city ?? GEOCODER_ERROR_MSG) + (res?.oaza ?? '') + (res?.koaza ?? '');
    }, []);

    /* **********************
     * 緯度経度のセット
     * **********************/
    useEffect(() => {
        if (!lngLat) { return; }

        (async () => {
            const res = await getReverseAddress({lng: lngLat[0], lat: lngLat[1]});
            setAddress(res);
        })();
    }, [lngLat, getReverseAddress]);


    if (!lngLat) { return <></>; }

    return (
        <div className='locationInfo'>
            {/* header */}
            <div className="locationInfo-header">
                <p className='locationInfo-title'>{address}</p>
                {/* 緯度経度 */}
                <p className='locationInfo-lngLat'>
                    {lngLat[1]},{lngLat[0]}
                </p>
            </div>

            <div className="locationInfo-content">
                {/* buttons */}
                <div className="buttonContainer">
                    <div className="buttonGroup">
                        <button className="iconBtn" onClick={() => onClickShareBtn(address)}>
                            <div className="icon"><BiSolidShareAlt /></div>
                            <span>シェア</span>
                        </button>
                        <button className="iconBtn" onClick={() => onClickStreetViewBtn(lngLat[0], lngLat[1])}>
                            <div className="icon"><BiStreetView /></div>
                            <span>ストリートビュー</span>
                        </button>
                        <button className="iconBtn" onClick={() => onClickGoToHereBtn(lngLat[0], lngLat[1])}>
                            <div className="icon"><BiWalk /></div>
                            <span>ここへ行く</span>
                        </button>
                    </div>
                </div>

                {tooltipText &&
                    <div className='tooltip'>{tooltipText}</div>
                }
            </div>
        </div>
    );
};

export default LocationInfo;
